/* ─────────────────────────────────────────────
   IMPORTAÇÃO DE ARQUIVOS DO BANCO (OFX, CSV, PDF)
   Funções puras (sem React): lêem o arquivo do banco e devolvem uma lista de
   movimentos { data, valor, descricao, descBanco, chave, importKey, aviso }.
   Convenção de sinal: valor NEGATIVO = dinheiro saindo (despesa / compra no cartão),
   valor POSITIVO = dinheiro entrando (receita, estorno, pagamento recebido no cartão).
   Saldos (inicial, do dia, final) e totais nunca viram movimento.
───────────────────────────────────────────── */

const MESES = { jan:1, fev:2, mar:3, abr:4, mai:5, jun:6, jul:7, ago:8, set:9, out:10, nov:11, dez:12 };

const pad2 = n => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const semAcento = s => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "");

/* ── Texto: decodifica o arquivo respeitando a codificação do banco ──
   Tenta UTF-8 estrito; se falhar (arquivos em Windows-1252/ISO-8859-1, comuns em OFX), usa 1252. */
export const decodificarTexto = buffer => {
  const bytes = new Uint8Array(buffer);
  try { return new TextDecoder("utf-8", { fatal: true }).decode(bytes); }
  catch { return new TextDecoder("windows-1252").decode(bytes); }
};

/* ── Chave de comparação de descrições (regras de classificação) ──
   Minúsculas, sem acento, sem números e sem pontuação. "Ebn*Canva0500" → "ebn canva". */
export const chaveDescricao = s => semAcento(s).toLowerCase().replace(/[^a-z]+/g, " ").replace(/\s+/g, " ").trim();

/* ── Descrição "limpa" para exibir e vincular, sem perder o texto do banco ──
   Remove máscaras de cartão/CPF, códigos entre aspas e os dados bancários da contraparte
   (CPF/CNPJ, banco, agência, conta). Mantém o tipo da operação + o nome da contraparte. */
const ehParteTecnica = p => {
  const letras = (p.match(/[A-Za-zÀ-ÿ]/g) || []).length;
  if (letras < 3) return true;
  if (/\d{2,3}\.\d{3}\.\d{3}|•{2,}|\*{3,}/.test(p)) return true;
  if (/\b(BCO|BANCO)\b|Ag[eê]ncia|\bConta:|\(\d{3,4}\)/i.test(p)) return true;
  return false;
};
export const simplificarDescricao = bruto => {
  let s = String(bruto || "").replace(/\s+/g, " ").trim();
  s = s.replace(/[•*]{2,}\s?\d{4}\s*/g, "");            // máscara de cartão "•••• 8155"
  s = s.replace(/:\s*"[^"]*"/g, "");
  s = s.replace(/\bDOC:?\s*\d+/gi, " ");                // "– DOC: 000000"
  s = s.replace(/(\D)\d{2}\/\d{2}(?=\s|$)/g, "$1");     // data colada no fim: "PIX TRANSF ALESSAN08/10"                    // códigos entre aspas: Boleto recebido: "112/908..."
  s = s.replace(/\b\d{1,2}\/\d{1,2}\s+\d{1,2}:\d{2}\b/g, " ").replace(/\s+/g, " ");   // "03/09 15:30" (complemento do BB)
  s = s.replace(/^[\s\-–]+|[\s\-–]+$/g, "");           // separadores soltos nas pontas
  const partes = s.split(/\s+[-–—]\s+|\s+[-–—]$|^[-–—]\s+/).map(p => p.trim()).filter(Boolean);
  const out = [];
  for (const p of partes) {
    if (ehParteTecnica(p)) break;                       // daqui em diante só vem documento/banco
    if (!out.some(o => o.toLowerCase() === p.toLowerCase())) out.push(p);
    if (out.length >= 3) break;
  }
  const r = out.join(" - ").trim();
  return r || s;
};

/* ── Regras de identificação de movimentos especiais ── */
export const RE_PAGTO_FATURA_CONTA = /pagamento de fatura|pagto\.? ?fatura|pgto\.? ?fatura|pag\.? ?fatura|fatura (do )?cart|pagamento cart[aã]o|pgto cart[aã]o/i;
export const RE_PAGTO_NO_CARTAO = /\bpagamentos?\b|pagto|pgto|pag fatura/i;
export const RE_APLICACAO = /\b(rdb|cdb|lci|lca|caixinhas?|cofrinhos?|poupan[cç]a|aplica[cç][aã]o|resgate|invest\s?f[aá]cil|porquinho)\b/i;
const RE_SALDO_OFX = /^\s*(s\s*a\s*l\s*d\s*o|saldo)\b/i;

/* ── Valor monetário ── */
const numBR = (inteiro, dec) => parseFloat(inteiro.replace(/\./g, "") + "." + dec);
export const parseNumero = raw => {
  let s = String(raw ?? "").trim().replace(/R\$\s?/i, "").replace(/\s/g, "").replace(/[−–]/g, "-");
  if (!s) return NaN;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  if (/D$/i.test(s)) { neg = true; s = s.slice(0, -1); } else if (/C$/i.test(s)) s = s.slice(0, -1);
  if (s.endsWith("-")) { neg = true; s = s.slice(0, -1); }
  if (s.startsWith("-")) { neg = !neg; s = s.slice(1); } else if (s.startsWith("+")) s = s.slice(1);
  const lc = s.lastIndexOf(","), ld = s.lastIndexOf(".");
  if (lc > ld) s = s.replace(/\./g, "").replace(",", ".");   // 1.234,56
  else s = s.replace(/,/g, "");                              // 1,234.56 ou 1234.56
  const v = parseFloat(s);
  return isNaN(v) ? NaN : (neg ? -v : v);
};

/* ═══════════════ OFX ═══════════════ */
const tagOFX = (bloco, tag) => {
  const m = bloco.match(new RegExp(`<${tag}>([^<\\r\\n]*)`, "i"));
  return m ? m[1].trim() : "";
};
const dataOFX = s => { const m = String(s).match(/^(\d{4})(\d{2})(\d{2})/); return m ? iso(m[1], +m[2], +m[3]) : null; };
const descOFX = (memo, name) => {
  const a = (memo || "").trim(), b = (name || "").trim();
  if (!a) return b;
  if (!b || a.toLowerCase().includes(b.toLowerCase())) return a;
  if (b.toLowerCase().includes(a.toLowerCase())) return b;
  return `${a} - ${b}`;
};

export const parseOFX = texto => {
  const ehCartao = /<CREDITCARDMSGSRSV1>|<CCSTMTRS>/i.test(texto);
  const banco = tagOFX(texto, "ORG") || tagOFX(texto, "BANKID") || "";
  const conta = tagOFX(texto, "ACCTID") || "";
  const blocos = texto.split(/<STMTTRN>/i).slice(1).map(b => b.split(/<\/STMTTRN>/i)[0]);
  const movs = [];
  blocos.forEach(b => {
    const valor = parseNumero(tagOFX(b, "TRNAMT"));
    const data = dataOFX(tagOFX(b, "DTPOSTED"));
    const memo = tagOFX(b, "MEMO"), name = tagOFX(b, "NAME");
    const descBanco = descOFX(memo, name);
    if (!data || isNaN(valor) || Math.abs(valor) < 0.005) return;
    if (RE_SALDO_OFX.test(descBanco)) return;              // alguns bancos mandam o saldo como transação
    const fitid = tagOFX(b, "FITID");
    movs.push({
      data, valor: Math.round(valor * 100) / 100, descBanco,
      importKey: fitid ? `ofx|${conta}|${fitid}|${valor.toFixed(2)}|${chaveDescricao(descBanco).slice(0, 24)}` : null,
    });
  });
  return { formato: "OFX", ehCartao, banco, movs: finalizar(movs, "ofx") };
};

/* ═══════════════ CSV ═══════════════ */
const dividirCSV = (linha, sep) => {
  const out = []; let cur = "", q = false;
  for (let i = 0; i < linha.length; i++) {
    const ch = linha[i];
    if (ch === '"') { if (q && linha[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
    else if (ch === sep && !q) { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map(c => c.trim());
};
const parseDataLivre = s => {
  const t = String(s || "").trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return iso(m[1], +m[2], +m[3]);
  m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/); if (m) return iso(m[3].length === 2 ? "20" + m[3] : m[3], +m[2], +m[1]);
  return null;
};

export const parseCSV = (texto, destinoCartao) => {
  const linhas = texto.replace(/^﻿/, "").split(/\r?\n/).filter(l => l.trim());
  if (!linhas.length) return { formato: "CSV", movs: [] };
  // Cabeçalho: primeira linha que tenha "data" e "valor/amount"
  let hi = linhas.findIndex(l => /data|date/i.test(l) && /valor|amount|quantia/i.test(l));
  if (hi < 0) hi = 0;
  const sep = [";", ",", "\t"].map(s => [s, dividirCSV(linhas[hi], s).length]).sort((a, b) => b[1] - a[1])[0][0];
  const head = dividirCSV(linhas[hi], sep).map(h => chaveDescricao(h));
  const idx = re => head.findIndex(h => re.test(h));
  const iData = idx(/^data|^date|lancamento/);
  const iValor = idx(/^valor|amount|quantia/);
  const iParcela = idx(/^parcela/);
  const ehCartao = head.some(h => /^(estabelecimento|portador|parcela|title|cartao|final do cartao)/.test(h)) ? true
    : head.some(h => /^(saldo|identificador|historico|documento)/.test(h)) ? false : undefined;
  const iDescs = head.map((h, i) => /descri|title|titulo|historico|estabelecimento|lancamento|detalhe|memo/.test(h) && i !== iData ? i : -1).filter(i => i >= 0);
  if (iData < 0 || iValor < 0) throw new Error("Não encontrei as colunas de data e valor no CSV.");
  const movs = [];
  linhas.slice(hi + 1).forEach(l => {
    const c = dividirCSV(l, sep);
    const data = parseDataLivre(c[iData]);
    const valor = parseNumero(c[iValor]);
    const parcela = iParcela >= 0 && /^\d+\s*\/\s*\d+$/.test(c[iParcela] || "") ? ` (${c[iParcela].replace(/\s/g, "")})` : "";
    const descBanco = [...new Set(iDescs.map(i => c[i]).filter(Boolean))].join(" - ") + parcela;
    if (!data || isNaN(valor) || Math.abs(valor) < 0.005) return;
    if (/saldo/i.test(descBanco) && !/pix|transf/i.test(descBanco)) return;
    movs.push({ data, valor, descBanco });
  });
  // Fatura de cartão em CSV costuma trazer compras positivas: inverte para "saída = negativo".
  if (destinoCartao && movs.filter(m => m.valor > 0).length > movs.filter(m => m.valor < 0).length) movs.forEach(m => { m.valor = -m.valor; });
  return { formato: "CSV", ehCartao, movs: finalizar(movs, "csv") };
};

/* ═══════════════ PDF ═══════════════
   1) No navegador, o pdf.js entrega pedaços de texto com posição (x, y).
   2) montarLinhas junta os pedaços em linhas visuais (mesmo y) e marca colunas (espaço grande = \t).
   3) parsePDF lê as linhas: data no início da linha, descrição e o primeiro valor monetário.
   Sinal: valor com "-", "−" ou "D" = saída; "+" ou "C" = entrada; sem sinal, vale a seção
   ("Total de entradas" / "Total de saídas", como no Nubank); sem seção, palavras-chave. */
const PDFJS_VER = "3.11.174";
const carregarPdfJs = () => new Promise((resolve, reject) => {
  if (typeof window === "undefined") return reject(new Error("PDF só pode ser lido no navegador."));
  if (window.pdfjsLib) return resolve(window.pdfjsLib);
  const s = document.createElement("script");
  s.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VER}/pdf.min.js`;
  s.onload = () => {
    if (!window.pdfjsLib) return reject(new Error("Não foi possível carregar o leitor de PDF."));
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VER}/pdf.worker.min.js`;
    resolve(window.pdfjsLib);
  };
  s.onerror = () => reject(new Error("Sem conexão para carregar o leitor de PDF. Tente novamente."));
  document.head.appendChild(s);
});

// Lê o PDF e devolve as páginas com os pedaços de texto e suas posições
// PDF protegido: a senha (geralmente dígitos do CPF) é usada só aqui, no navegador, e não é guardada.
export class ErroSenhaPDF extends Error {
  constructor(incorreta) { super(incorreta ? "Senha incorreta. Confira e tente de novo." : "Esse PDF é protegido por senha."); this.incorreta = !!incorreta; }
}
export const lerPaginasPDF = async (buffer, senha) => {
  const pdfjs = await carregarPdfJs();
  let doc;
  try { doc = await pdfjs.getDocument({ data: new Uint8Array(buffer), ...(senha ? { password: senha } : {}) }).promise; }
  catch (e) {
    if (e?.name === "PasswordException" || /password/i.test(e?.message || "")) throw new ErroSenhaPDF(e?.code === 2 || (!!senha && e?.code !== 1));
    throw new Error("Não foi possível abrir o PDF.");
  }
  const paginas = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const tc = await page.getTextContent();
    paginas.push({ items: tc.items.map(it => ({ str: it.str, x: it.transform[4], y: it.transform[5], w: it.width, h: Math.abs(it.transform[3]) || it.height || 8 })) });
  }
  return paginas;
};

export const montarLinhas = paginas => {
  const linhas = [];
  paginas.forEach((pg, pi) => {
    const items = pg.items.filter(i => i.str && i.str.trim()).sort((a, b) => b.y - a.y || a.x - b.x);
    const grupos = [];
    items.forEach(it => {
      const g = grupos.find(g => Math.abs(g.y - it.y) <= Math.max(2, Math.min(g.h, it.h) * 0.45));
      if (g) { g.items.push(it); g.h = Math.max(g.h, it.h); } else grupos.push({ y: it.y, h: it.h, items: [it] });
    });
    grupos.sort((a, b) => b.y - a.y).forEach(g => {
      const its = g.items.sort((a, b) => a.x - b.x);
      const cells = [];
      its.forEach(it => {
        const ult = cells[cells.length - 1];
        const gap = ult ? it.x - ult.fim : Infinity;
        if (ult && gap < Math.max(6, g.h * 1.1)) { ult.text += (gap > g.h * 0.15 ? " " : "") + it.str; ult.fim = it.x + it.w; }
        else cells.push({ x: it.x, fim: it.x + it.w, text: it.str });
      });
      const cl = cells.map(c => ({ x: c.x, text: c.text.replace(/\s+/g, " ").trim() })).filter(c => c.text);
      if (cl.length) linhas.push({ page: pi, y: g.y, h: g.h, minX: cl[0].x, cells: cl, text: cl.map(c => c.text).join("\t") });
    });
  });
  return linhas;
};

// Valor monetário brasileiro com sinal opcional antes/depois. Ignora percentuais e números sem centavos.
const RE_VALOR = /(?<![\w.,/])([+\-−–] ?)?(R\$ ?)?([+\-−–] ?)?(\d{1,3}(?:\.\d{3})+|\d+),(\d{2})(?![\d%])(?:\s?([CD])(?![A-Za-zÀ-ÿ]))?(?!\s?%)/g;
const valoresDaLinha = texto => {
  const out = []; let m;
  RE_VALOR.lastIndex = 0;
  while ((m = RE_VALOR.exec(texto))) {
    const sinal = (m[1] || m[3] || "").trim();
    const neg = /[-−–]/.test(sinal) || m[6] === "D";
    const pos = sinal === "+" || m[6] === "C";
    out.push({ abs: numBR(m[4], m[5]), sinal: neg ? -1 : pos ? 1 : 0, ini: m.index });
  }
  return out;
};
const RE_DATA_NUM = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?(?![\d/])/;
const RE_DATA_TXT = /^(\d{1,2})\s+(?:de\s+)?([A-Za-zçÇ]{3,9})\.?(?:\s+(?:de\s+)?(\d{4}))?(?![\w])/i;
const dataNoInicio = texto => {
  const t = texto.trim();
  let m = t.match(RE_DATA_NUM);
  if (m && +m[2] >= 1 && +m[2] <= 12 && +m[1] >= 1 && +m[1] <= 31)
    return { d: +m[1], m: +m[2], y: m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : null, len: m[0].length };
  m = t.match(RE_DATA_TXT);
  if (m) { const mes = MESES[semAcento(m[2]).toLowerCase().slice(0, 3)]; if (mes && +m[1] >= 1 && +m[1] <= 31) return { d: +m[1], m: mes, y: m[3] ? +m[3] : null, len: m[0].length }; }
  return null;
};
// Data de referência do documento (a mais recente com ano), usada para completar datas sem ano
const dataReferencia = linhas => {
  let ref = null;
  const re = /(\d{1,2})[/.](\d{1,2})[/.](\d{4})|(\d{1,2})\s+(?:de\s+)?([A-Za-zçÇ]{3,9})\.?\s+(?:de\s+)?(\d{4})/gi;
  linhas.forEach(l => { let m; re.lastIndex = 0; while ((m = re.exec(l.text))) {
    const d = m[1] ? iso(m[3], +m[2], +m[1]) : (MESES[semAcento(m[5]).toLowerCase().slice(0, 3)] ? iso(m[6], MESES[semAcento(m[5]).toLowerCase().slice(0, 3)], +m[4]) : null);
    if (d && (!ref || d > ref)) ref = d;
  } });
  return ref;
};
const completarAno = (dt, ref) => {
  if (dt.y) return iso(dt.y, dt.m, dt.d);
  const refY = ref ? +ref.slice(0, 4) : new Date().getFullYear();
  let d = iso(refY, dt.m, dt.d);
  if (ref && d > ref && (+d.slice(5, 7) - +ref.slice(5, 7) + 12) % 12 > 1) d = iso(refY - 1, dt.m, dt.d);
  return d;
};

const RE_PULAR_CONTA = /saldo|s a l d o|subtotal|limite (total|dispon|de cr|utiliz)|total a pagar|pagamento m[ií]nimo|total de entradas|total de sa[ií]das|total de cr[eé]ditos|total de d[eé]bitos|rendimento l[ií]quido|valores em r\$|extrato gerado|^valor\b|saldo por transa/i;
const RE_PULAR_CARTAO = /total|saldo|limite|pagamento m[ií]nimo|parcel(ar|e) |juros rotativo|vencimento|per[ií]odo|valor m[aá]ximo|cet\b|convers[aã]o/i;
const RE_ENTRADA = /recebid|recebimento|cr[eé]dito|dep[oó]sito|resgate|estorno|reembolso|rendimento|\brend\b|devolu|sal[aá]rio/i;
// "03/09 15:30 Fulano": linha de complemento (data + hora), não é um novo dia
const RE_DATA_HORA = /^\s*\d{1,2}\/\d{1,2}\s+\d{1,2}:\d{2}/;

/* ── Fatura de cartão ou extrato de conta? (pelo conteúdo do documento) ──
   true = fatura de cartão, false = extrato de conta, undefined = não deu para saber. */
const SINAIS_CARTAO = [/total a pagar/i, /pagamento m[ií]nimo/i, /limite (total|dispon[ií]vel|de cr[eé]dito|utilizado)/i, /vencimento/i, /fatura/i, /melhor (data|dia) (de|para) compra/i, /encargos|juros rotativo|rotativo/i, /cart[aã]o (de cr[eé]dito|final)|final \d{4}/i];
const SINAIS_CONTA = [/saldo (do dia|anterior|inicial|final|em conta|dispon[ií]vel|atual|bloqueado)/i, /\bextrato\b/i, /conta corrente/i, /total de entradas|total de sa[ií]das/i];
export const detectarCartao = texto => {
  const c = SINAIS_CARTAO.filter(r => r.test(texto)).length;
  const k = SINAIS_CONTA.filter(r => r.test(texto)).length;
  if (c >= 3 && c > k + 1) return true;
  if (k >= 1 && c <= 2) return false;
  return undefined;
};

export const parsePDF = (linhas, destinoCartao) => {
  const ref = dataReferencia(linhas);
  const movs = [];
  let dataAtual = null, secao = 0, ult = null, ultLinha = null, buffer = [];
  const totais = { entradas: null, saidas: null, totalPagar: null, faturaAnterior: null, pagamentoRecebido: null };
  linhas.forEach(l => {
    const vals = valoresDaLinha(l.text);
    // Totais do documento (para a conferência)
    if (vals.length === 1) {            // só linhas com um único valor (tabelas de simulação têm vários)
      if (/total de entradas|total de cr[eé]ditos/i.test(l.text)) totais.entradas = Math.max(totais.entradas || 0, vals[vals.length - 1].abs);
      if (/total de sa[ií]das|total de d[eé]bitos/i.test(l.text)) totais.saidas = Math.max(totais.saidas || 0, vals[vals.length - 1].abs);
      if (/total a pagar/i.test(l.text) && totais.totalPagar == null) totais.totalPagar = vals[vals.length - 1].abs;
      if (/fatura anterior/i.test(l.text) && totais.faturaAnterior == null) totais.faturaAnterior = vals[vals.length - 1].abs;
      if (/pagamentos? recebido/i.test(l.text) && totais.pagamentoRecebido == null) totais.pagamentoRecebido = vals[vals.length - 1].abs;
    }
    // Seção de entradas/saídas (layout do Nubank e similares)
    if (/total de entradas|^\s*entradas\b|total de cr[eé]ditos/i.test(l.text.replace(/^\S+\s\S+\s\d{4}\s/, ""))) secao = 1;
    if (/total de sa[ií]das|^\s*sa[ií]das\b|total de d[eé]bitos/i.test(l.text.replace(/^\S+\s\S+\s\d{4}\s/, ""))) secao = -1;

    const dt = RE_DATA_HORA.test(l.text) ? null : dataNoInicio(l.text);
    let resto = l.text;
    // Cabeçalho de período ("01 DE SETEMBRO DE 2026 a 30 DE SETEMBRO DE 2026") não muda a data corrente
    const ehPeriodo = (l.text.match(/\d{4}/g) || []).length >= 2 && /\sa\s|\sat[eé]\s/i.test(l.text);
    if (dt && !ehPeriodo) { dataAtual = completarAno(dt, ref); resto = l.text.trim().slice(dt.len).replace(/^[\t\s-]+/, ""); }
    const valsResto = valoresDaLinha(resto);
    const letras = (resto.split(/(?:R\$)?\s?[+\-−–]?\s?\d{1,3}(?:\.\d{3})*,\d{2}/)[0].match(/[A-Za-zÀ-ÿ]/g) || []).length;
    const pular = destinoCartao ? RE_PULAR_CARTAO.test(resto) : RE_PULAR_CONTA.test(resto);
    const podeSerMov = valsResto.length && letras >= 3 && !pular && dataAtual && (destinoCartao ? !!dt : true);

    // Linha com data + valor mas sem descrição: a descrição está na(s) linha(s) logo acima (buffer)
    const semDesc = !podeSerMov && valsResto.length && letras < 3 && !pular && dataAtual && dt && buffer.length
      && l.page === buffer[buffer.length - 1].page && (buffer[buffer.length - 1].y - l.y) < l.h * 3;
    if (podeSerMov || semDesc) {
      const v = valsResto[0];
      const descCells = semDesc ? buffer.map(b => b.text.replace(/\t/g, " ")) : resto.slice(0, v.ini).split("\t").map(s => s.trim()).filter(Boolean);
      const descBanco = descCells.join(" - ").replace(/\s*-\s*$/, "").trim();
      let sinal;
      if (destinoCartao) sinal = v.sinal < 0 ? 1 : -1;       // no cartão: compra = saída; valor negativo = crédito/pagamento
      else sinal = v.sinal || secao || (RE_ENTRADA.test(descBanco) ? 1 : -1);
      ult = { data: dataAtual, valor: sinal * v.abs, descBanco, sinalBruto: v.sinal, semSinal: !destinoCartao && !v.sinal && !secao, page: l.page,
        descX: semDesc ? buffer[0].minX - 6 : ((l.cells.find(c => descBanco.startsWith(c.text.slice(0, 6)))?.x) ?? l.minX),
        contRestantes: semDesc ? 1 : 4, gapMax: semDesc ? 1.9 : 3.2 };
      movs.push(ult);
      ultLinha = l; buffer = [];
      return;
    }
    // Continuação da descrição (linha indentada logo abaixo, sem valor e sem data)
    const ehContinuacao = !destinoCartao && ult && ult.contRestantes > 0 && !dt && !vals.length && !pular && l.page === ult.page && ultLinha
      && l.minX > ult.descX + 4 && (ultLinha.y - l.y) < Math.max(l.h, ultLinha.h) * ult.gapMax;
    if (ehContinuacao) { ult.descBanco = `${ult.descBanco} ${l.text.replace(/\t/g, " ")}`.replace(/\s+/g, " ").trim(); ult.contRestantes--; ultLinha = l; return; }
    ult = null; ultLinha = null;
    // Guarda linhas de texto sem valor: podem ser a descrição de um movimento cujo valor vem na linha seguinte
    const ehTexto = !destinoCartao && !dt && !vals.length && !pular && (l.text.match(/[A-Za-zÀ-ÿ]/g) || []).length >= 3;
    const colado = buffer.length && buffer[buffer.length - 1].page === l.page && (buffer[buffer.length - 1].y - l.y) < l.h * 1.9;
    buffer = ehTexto ? (colado ? [...buffer, l].slice(-2) : [l]) : [];
  });
  // Banco que marca saída com "-" (Itaú e outros): valor sem sinal no mesmo extrato é entrada.
  if (!destinoCartao && movs.some(m => !m.semSinal && m.valor < 0) && !movs.some(m => !m.semSinal && m.valor > 0))
    movs.forEach(m => { if (m.semSinal) m.valor = Math.abs(m.valor); });
  // Fatura: o sinal mais frequente no documento é o das compras; o diferente (ou pagamento/estorno no texto) é crédito.
  if (destinoCartao && movs.length) {
    const cont = { "-1": 0, "0": 0, "1": 0 };
    movs.forEach(m => { cont[String(m.sinalBruto)]++; });
    const maj = +Object.entries(cont).sort((a, b) => b[1] - a[1])[0][0];
    movs.forEach(m => {
      const credito = m.sinalBruto !== maj || RE_PAGTO_NO_CARTAO.test(m.descBanco) || /estorno|cr[eé]dito|devolu|reembolso|cashback/i.test(m.descBanco);
      m.valor = (credito ? 1 : -1) * Math.abs(m.valor);
    });
  }
  movs.forEach(m => { delete m.sinalBruto; delete m.semSinal; delete m.page; delete m.descX; delete m.contRestantes; delete m.gapMax; });
  return { formato: "PDF", ehCartao: detectarCartao(linhas.map(l => l.text).join("\n")), movs: finalizar(movs, "pdf"), totais };
};

/* ── Conferência: soma do que foi lido x totais impressos no documento ── */
export const conferirTotais = (movs, totais, destinoCartao) => {
  if (!totais) return null;
  const cent = v => Math.round(v * 100);
  if (destinoCartao) {
    if (totais.totalPagar == null) return null;
    const esperado = totais.totalPagar - (totais.faturaAnterior || 0) + (totais.pagamentoRecebido || 0);
    const lido = -movs.filter(m => !RE_PAGTO_NO_CARTAO.test(m.descBanco)).reduce((a, m) => a + m.valor, 0);
    return { ok: Math.abs(cent(esperado) - cent(lido)) <= 2, itens: [{ rotulo: "Compras e encargos da fatura", esperado, lido }] };
  }
  const itens = [];
  if (totais.entradas != null) itens.push({ rotulo: "Entradas", esperado: totais.entradas, lido: movs.filter(m => m.valor > 0).reduce((a, m) => a + m.valor, 0) });
  if (totais.saidas != null) itens.push({ rotulo: "Saídas", esperado: totais.saidas, lido: -movs.filter(m => m.valor < 0).reduce((a, m) => a + m.valor, 0) });
  if (!itens.length) return null;
  return { ok: itens.every(i => Math.abs(cent(i.esperado) - cent(i.lido)) <= 2), itens };
};

/* ── Acabamento comum: descrição limpa, chave de regra e chave de importação ── */
const finalizar = (movs, origem) => {
  const vistos = {};
  return movs.map(m => {
    const descricao = simplificarDescricao(m.descBanco);
    const chave = chaveDescricao(descricao);
    let importKey = m.importKey;
    if (!importKey) {
      const base = `${origem}|${m.data}|${m.valor.toFixed(2)}|${chave.slice(0, 30)}`;
      vistos[base] = (vistos[base] || 0) + 1;
      importKey = `${base}|${vistos[base]}`;
    }
    return { ...m, valor: Math.round(m.valor * 100) / 100, descricao, chave, importKey };
  }).sort((a, b) => a.data.localeCompare(b.data));
};

/* ── Entrada única: identifica o formato pelo nome/conteúdo ── */
export const lerArquivoBanco = async (file, destinoCartao, senha) => {
  const buffer = await file.arrayBuffer();
  const nome = (file.name || "").toLowerCase();
  const inicio = new TextDecoder("latin1").decode(new Uint8Array(buffer).slice(0, 5));
  if (nome.endsWith(".pdf") || inicio === "%PDF-") {
    const paginas = await lerPaginasPDF(buffer, senha);
    const linhas = montarLinhas(paginas);
    if (!linhas.length) throw new Error("Esse PDF não tem texto (parece ser uma imagem escaneada). Baixe o extrato pelo app ou internet banking.");
    return parsePDF(linhas, destinoCartao);
  }
  const texto = decodificarTexto(buffer);
  if (/<OFX>|OFXHEADER/i.test(texto)) return parseOFX(texto);
  if (nome.endsWith(".csv") || nome.endsWith(".txt")) return parseCSV(texto, destinoCartao);
  throw new Error("Formato não reconhecido. Use OFX, CSV ou PDF do banco.");
};
